import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Phone, ScanLine } from "lucide-react";

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [step, setStep] = useState(1); // 1: phone/name, 2: otp
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submitStep1 = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/register", { phone, full_name: fullName, lang_pref: "en" });
        setDevOtp(data.dev_otp);
        toast.success("Account created. Verify your phone.");
      } else {
        const { data } = await api.post("/auth/request-otp", { phone });
        setDevOtp(data.dev_otp);
        toast.success("OTP sent.");
      }
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, otp });
      login(data.token, data.user);
      toast.success("Welcome to Qaab.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F9F8]">
      {/* Left visual */}
      <div className="hidden lg:flex bg-[#111827] text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo-link">
          <div className="w-7 h-7 bg-white text-[#002FA7] grid place-items-center font-display font-extrabold text-sm">Q</div>
          <span className="font-display font-extrabold tracking-tight text-lg">QAAB</span>
        </Link>
        <div>
          <div className="label-eyebrow text-gray-400 mb-4">Why Qaab</div>
          <h2 className="font-display font-black text-4xl tracking-tight leading-tight mb-6">
            Your number<br />stays private.<br />Your car becomes<br />reachable.
          </h2>
          <p className="text-gray-400 max-w-md leading-relaxed">
            Sign in with your phone — that's it. We use OTP to verify, never share. Identity stays in our vault.
          </p>
        </div>
        <div className="font-mono text-xs text-gray-500">// secure phone-based authentication</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-gray-600 mb-8" data-testid="back-home">
            <ArrowLeft size={14} /> Back home
          </Link>

          {step === 1 && (
            <>
              <div className="label-eyebrow mb-3">{mode === "register" ? "Create account" : "Welcome back"}</div>
              <h1 className="font-display font-black text-4xl tracking-tight mb-2">
                {mode === "register" ? "Activate Qaab." : "Sign in."}
              </h1>
              <p className="text-gray-600 mb-10">
                {mode === "register" ? "Enter your details. We'll send a 6-digit code." : "Enter your phone. We'll send a 6-digit code."}
              </p>

              <form onSubmit={submitStep1} className="space-y-5">
                {mode === "register" && (
                  <div>
                    <label className="label-eyebrow block mb-2">Full name</label>
                    <input
                      data-testid="register-name-input"
                      className="input-flat"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      placeholder="Mohammed Al Dosari"
                    />
                  </div>
                )}
                <div>
                  <label className="label-eyebrow block mb-2">Phone</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      data-testid="phone-input"
                      className="input-flat pl-10"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      placeholder="+973 3xxx xxxx"
                      type="tel"
                    />
                  </div>
                </div>

                <button type="submit" disabled={busy} className="btn btn-primary w-full" data-testid="submit-phone-button">
                  {busy ? "Sending..." : "Send code"} <ArrowRight size={16} />
                </button>
              </form>

              <div className="mt-8 text-sm text-gray-600 text-center">
                {mode === "register" ? (
                  <>Already have an account? <button onClick={() => setMode("login")} className="text-[#002FA7] font-medium" data-testid="switch-to-login">Sign in</button></>
                ) : (
                  <>New to Qaab? <button onClick={() => setMode("register")} className="text-[#002FA7] font-medium" data-testid="switch-to-register">Create account</button></>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="label-eyebrow mb-3">Verify code</div>
              <h1 className="font-display font-black text-4xl tracking-tight mb-2">Enter the 6-digit code.</h1>
              <p className="text-gray-600 mb-4">Sent to <span className="font-mono">{phone}</span></p>

              {devOtp && (
                <div className="border border-yellow-300 bg-yellow-50 p-3 mb-6 font-mono text-sm" data-testid="dev-otp-banner">
                  DEV MODE — OTP: <span className="font-bold">{devOtp}</span>
                </div>
              )}

              <form onSubmit={submitOtp} className="space-y-5">
                <input
                  data-testid="otp-input"
                  className="input-flat text-center text-2xl font-mono tracking-[0.5em]"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  inputMode="numeric"
                  placeholder="------"
                />
                <button type="submit" disabled={busy || otp.length !== 6} className="btn btn-primary w-full" data-testid="verify-otp-button">
                  {busy ? "Verifying..." : "Verify & enter"} <ScanLine size={16} />
                </button>
                <button type="button" onClick={() => { setStep(1); setOtp(""); setDevOtp(""); }} className="w-full text-sm text-gray-600 hover:text-[#002FA7]" data-testid="back-to-phone">
                  ← Use different phone
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
